/* eslint-disable no-bitwise */
import * as struct from 'python-struct';
import { crc16 } from './modbus.utils';

export class SolarmanWrapper {
  private readonly model: 'WIFI' | 'ETH' = 'WIFI';
  private readonly v5Start: Uint8Array;
  private v5Length: Uint8Array;
  private readonly v5ControlCode: Uint8Array;
  private readonly v5ResponseControlCode: Uint8Array;
  private readonly v5Serial: Uint8Array;
  private readonly v5LoggerSerial: Uint8Array;
  private readonly v5FrameType: Uint8Array;
  private readonly v5SensorType: Uint8Array;
  private readonly v5DeliveryTime: Uint8Array;
  private readonly v5PowerOnTime: Uint8Array;
  private readonly v5OffsetTime: Uint8Array;
  private readonly v5Checksum: Uint8Array;
  private readonly v5End: Uint8Array;

  constructor(serial: number, model?: 'WIFI' | 'ETH') {
    this.v5Start = new Uint8Array([0xa5]);
    this.v5Length = new Uint8Array([0x00, 0x00]);
    this.v5ControlCode = struct.pack('<H', 0x4510);
    this.v5ResponseControlCode = struct.pack('<H', 0x1510);
    this.v5Serial = new Uint8Array([0x00, 0x00]);
    this.v5LoggerSerial = struct.pack('<I', serial);
    this.v5FrameType = new Uint8Array([0x02]);
    this.v5SensorType = new Uint8Array([0x00, 0x00]);
    this.v5DeliveryTime = new Uint8Array([0x00, 0x00, 0x00, 0x00]);
    this.v5PowerOnTime = new Uint8Array([0x00, 0x00, 0x00, 0x00]);
    this.v5OffsetTime = new Uint8Array([0x00, 0x00, 0x00, 0x00]);
    this.v5Checksum = new Uint8Array([0x00]);
    this.v5End = new Uint8Array([0x15]);

    this.model = model ?? this.model;
  }

  private static calculateV5FrameChecksum(frame: Uint8Array): number {
    let checksum = 0;
    for (let i = 1; i < frame.length - 2; i++) {
      checksum += frame[i] & 0xff;
    }
    return checksum & 0xff;
  }

  v5FrameEncoder(modbusFrame: Uint8Array): Uint8Array {
    if (this.model === 'WIFI') {
      return this.encodeWIFI(modbusFrame);
    }
    return this.encodeETH(modbusFrame);
  }

  v5FrameDecoder(v5Frame: Uint8Array): Uint8Array {
    if (this.model === 'WIFI') {
      return this.decodeWIFI(v5Frame);
    }
    return this.decodeETH(v5Frame);
  }

  encodeWIFI(modbusFrame: Uint8Array): Uint8Array {
    this.v5Length = struct.pack('<H', 15 + modbusFrame.length);

    const v5Header = new Uint8Array([
      ...this.v5Start,
      ...this.v5Length,
      ...this.v5ControlCode,
      ...this.v5Serial,
      ...this.v5LoggerSerial,
    ]);

    const v5Payload = new Uint8Array([
      ...this.v5FrameType,
      ...this.v5SensorType,
      ...this.v5DeliveryTime,
      ...this.v5PowerOnTime,
      ...this.v5OffsetTime,
      ...modbusFrame,
    ]);

    const v5Trailer = new Uint8Array([...this.v5Checksum, ...this.v5End]);

    const v5Frame = new Uint8Array([
      ...v5Header,
      ...v5Payload,
      ...v5Trailer,
    ]);

    v5Frame[v5Frame.length - 2] = SolarmanWrapper.calculateV5FrameChecksum(v5Frame);
    return v5Frame;
  }

  decodeWIFI(v5Frame: Uint8Array): Uint8Array {
    const frameLen = v5Frame.length;

    // Use a DataView to read the value of payloadLen
    const view = new DataView(v5Frame.buffer, v5Frame.byteOffset, v5Frame.byteLength);
    const payloadLen = view.getUint16(1, true); // true = little endian

    const frameLenWithoutPayloadLen = 13;

    if (frameLen !== frameLenWithoutPayloadLen + payloadLen) {
      console.warn('frame_len does not match payload_len.');
      return new Uint8Array();
    }

    if (
      v5Frame[0] !== this.v5Start[0] ||
      v5Frame[frameLen - 1] !== this.v5End[0]
    ) {
      console.warn('V5 frame contains invalid start or end values');
      return new Uint8Array();
    }

    if (v5Frame[frameLen - 2] !== SolarmanWrapper.calculateV5FrameChecksum(v5Frame)) {
      console.warn('V5 frame contains invalid V5 checksum');
      return new Uint8Array();
    }

    if (!v5Frame.slice(7, 11).every((val, i) => val === this.v5LoggerSerial[i])) {
      console.warn('V5 frame contains incorrect data logger serial number');
      return new Uint8Array();
    }

    if (!v5Frame.slice(3, 5).every((val, i) => val === this.v5ResponseControlCode[i])) {
      console.warn('V5 frame contains incorrect control code');
      return new Uint8Array();
    }

    if (v5Frame[11] !== this.v5FrameType[0]) {
      console.warn('V5 frame contains invalid frametype');
      return new Uint8Array();
    }

    const modbusFrame = v5Frame.slice(25, frameLen - 2);

    if (modbusFrame.length < 5) {
      console.warn('V5 frame does not contain a valid Modbus RTU frame');
      return new Uint8Array();
    }

    return modbusFrame;
  }

  encodeETH(modbusFrame: Uint8Array): Uint8Array {
    const packet = new Uint8Array(modbusFrame.length + 6 - 2);
    packet.set([0x03, 0xED, 0x00, 0x00, 0x00]);
    packet[5] = modbusFrame.length - 2;
    for (let i = 0; i < modbusFrame.length - 2; i++) {
      packet[i + 6] = modbusFrame[i];
    }

    return packet;
  }

  /**
   * @important Support G3 only
   */
  decodeETH(modbusFrame: Uint8Array): Uint8Array {
    /** Remove the first 6 bytes (header) */
    const response = modbusFrame.slice(6);

    /** Verify that the frame contains at least the address and function code */
    if (response.length < 2) {
      throw new Error("Invalid Modbus frame");
    }

    const functionCode = response[1];

    /** Case 2: Read response (function code 0x03) - G2/G3 */
    /** Case 2: Read response (function code 0x04) - G2 */
    /** Case 2: Read response (function code 0x50) - G2 */
    if (functionCode === 0x03 || functionCode === 0x04 || functionCode === 0x50 || (
      functionCode === 0x10
    )) {
      /** The read response structure is:
       * [address, function, byteCount, ...data]
       */
      /** Verify that there are at least 3 bytes (up to the byte count) */
      if (response.length < 3) {
        throw new Error("Incomplete read frame");
      }
      const byteCount = response[2];
      const expectedLength = 3 + byteCount; /** address + function + byteCount + data */

      if (response.length < expectedLength) {
        throw new Error("Incomplete read frame: missing data");
      }

      /** Extract only the relevant bytes */
      const trimmedResponse = response.slice(0, expectedLength);

      /** Compute CRC for the read frame (excluding the CRC itself) */
      const { lowByte, highByte } = crc16(trimmedResponse);

      /** Create a new buffer adding the CRC */
      const responseWithCrc = new Uint8Array(trimmedResponse.length + 2);
      responseWithCrc.set(trimmedResponse, 0);
      responseWithCrc[trimmedResponse.length] = lowByte;
      responseWithCrc[trimmedResponse.length + 1] = highByte;

      return responseWithCrc;
    } else {
      throw new Error("Unrecognized Modbus frame structure");
    }
  }
}
